import React, { useState } from 'react'
import { ChevronDown, ChevronUp, Copy, FileText, AlertTriangle, Shield } from 'lucide-react'

const DocumentSummary = ({ summary = {}, title = '' }) => {
  const [open, setOpen] = useState(true)

  if (!summary || (!summary.bullets && !summary.keyClauses)) return null

  const handleCopy = async () => {
    const text = [
      `Document: ${title}`,
      `Complexity: ${summary.complexityRating}`,
      `Risk: ${summary.riskAssessment}`,
      '',
      'Key points:',
      ... (summary.bullets || []),
      '',
      'Key clauses:',
      ... (summary.keyClauses || [])
    ].join('\n')
    try { await navigator.clipboard.writeText(text); alert('Summary copied') } catch { alert('Copy failed') }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 max-w-4xl mx-auto w-full mb-6 overflow-hidden">
      <div
        className="flex justify-between items-center p-4 bg-gray-50/50 border-b border-gray-100 cursor-pointer"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
            <FileText size={18} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Executive Summary</h3>
            {title && <div className="text-xs text-gray-500 font-medium">{title}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleCopy() }}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Copy to clipboard"
          >
            <Copy size={16} />
          </button>
          <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
            {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="p-5 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Complexity</div>
              <div className="font-medium text-gray-900 flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${summary.complexityRating === 'High' ? 'bg-red-500' :
                  summary.complexityRating === 'Medium' ? 'bg-yellow-500' : 'bg-green-500'
                  }`} />
                {summary.complexityRating}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Risk Assessment</div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm font-medium">
                  <span className={`${summary.riskAssessment.includes('High') ? 'text-red-600' : summary.riskAssessment.includes('Moderate') ? 'text-yellow-600' : 'text-green-600'}`}>
                    {summary.riskAssessment}
                  </span>
                  <span className="text-gray-400 text-xs">
                    {summary.riskAssessment.includes('High') ? 'Critical' : summary.riskAssessment.includes('Moderate') ? 'Caution' : 'Safe'}
                  </span>
                </div>
                <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${summary.riskAssessment.includes('High') ? 'bg-red-500 w-[90%]' :
                        summary.riskAssessment.includes('Moderate') ? 'bg-yellow-500 w-[60%]' :
                          'bg-green-500 w-[20%]'
                      }`}
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-blue-600 rounded-full"></span>
              Key Highlights
            </h4>
            <ul className="space-y-2">
              {(summary.bullets || []).map((b, i) => (
                <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 bg-gray-300 rounded-full flex-shrink-0"></span>
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-indigo-600 rounded-full"></span>
              Critical Clauses
            </h4>
            <div className="flex flex-wrap gap-2">
              {(summary.keyClauses || []).map((c, i) => (
                <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DocumentSummary
